import { Constraint, FlexGrid, Form, Input, Modal, Text, Tooltip, Checkbox, Header, Button } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Redirect } from 'react-router-dom';
import { PatternFormat, NumericFormat } from 'react-number-format';
import { format, parse } from 'date-fns';
import { faInfo } from '@fortawesome/free-solid-svg-icons/faInfo';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import InputWithValidation from '../InputWithValidation';
import getErrorMessage from '../../../lib/utils/getErrorMessage';
import isPostalCodeFromPayCountry from '../../../lib/utils/isPostalCodeFromPayCountry';
import useEffectWithDeepCompare from '../../../lib/utils/useEffectWithDeepCompare';
import { ANALYTICS_SCREENS } from '../../constants/analytics';
import PayCountries from '../../constants/payCountries';
import PayPrograms from '../../constants/payPrograms';
import useTracking from '../../hooks/useTracking';
import AddressPropType from '../../prop-types/address';
import ProgramPropType from '../../prop-types/program';
import { ShippingAddressPropType } from '../../prop-types/shipping-address';
import DeliverableAddressConfirmation from '../DeliverableAddressConfirmation';
import FormActionButtons from '../FormActionButtons';
import PostalCodeInput from '../PostalCodeInput';
import StatesDropdown from '../StatesDropdown';
import UndeliverableAddressConfirmation from '../UndeliverableAddressConfirmation';
import isValidBirthdate, { isValidAge } from '../../../lib/utils/isValidBirthdate';
import isValidLicense from '../../../lib/utils/isValidLicense';
import styles from './ShippingAddressForm.module.css';
import { ANNUAL_INCOME_FEATURE_VARIANT } from '../../constants';
import SupportLink from '../SupportLink';

const cx = classnames.bind(styles);

const BIRTHDATE_ERROR = 'Please enter a valid birth date (mm/dd/yyyy)';
const NUMBER_ERROR = 'Please enter a valid license number';
const INPUT_DATE_REGEX = /^(0[1-9]|1[012])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
const INPUT_INCOME_REGEX = /^\d+$/;
const ANNUAL_INCOME_ERROR = 'Annual household income needs to be more than $5';

function isEqual(a, b) {
  return (a || '').toUpperCase() === (b || '').toUpperCase();
}

function addressesMatch(addressA = {}, addressB = {}) {
  return isEqual(addressA.line_1, addressB.line_1)
      && isEqual(addressA.line_2, addressB.line_2)
      && isEqual(addressA.locality, addressB.locality)
      && isEqual(addressA.region, addressB.region)
      && isEqual(addressA.postal_code, addressB.postal_code);
}

const checkDateValidity = inputDate => inputDate && isValidBirthdate(inputDate);
const isAboveMinimumAge = inputDate => inputDate && isValidAge(inputDate);

export default function ShippingAddressForm({
  address: initialAddress,
  annualIncomeInputType,
  analyticsScreenName,
  basePath,
  defaultPostalCode,
  email: defaultEmail,
  error,
  errorFetchingLocationInfo,
  fetchLocationInfo,
  firstName: initialFirstName,
  isEnrolling,
  isFetchingLocationInfo,
  isUpdating,
  isValidating,
  lastName: initialLastName,
  locationInfo,
  primaryButtonText,
  program,
  resetAddressError,
  result,
  subheader,
  saveShippingAddress,
  token,
  triggerPrescreen,
  updateCustomerProfile,
  validateShippingAddress,
  ...rest
}) {
  const [recaptchaResponse, setRecaptchaResponse] = useState(null);
  const [firstName, setFirstName] = useState(initialFirstName || '');
  const [firstNameError, setFirstNameError] = useState();
  const [lastName, setLastName] = useState(initialLastName || '');
  const [lastNameError, setLastNameError] = useState();
  const [customerDetails, setCustomerDetails] = React.useState({
    number: '',
    birthdate: '',
    annualIncome: undefined,
    incomeConfirmed: undefined,
  });
  const [customerDetailErrors, setCustomerDetailErrors] = React.useState({
    number: '',
    birthdate: '',
    annualIncome: undefined,
    incomeConfirmed: undefined,
  });
  const [incomeConfirmed, setIncomeConfirmed] = React.useState(false);
  const [line1, setLine1] = useState(initialAddress?.line_1 || '');
  const [line2, setLine2] = useState(initialAddress?.line_2 || '');
  const [locality, setLocality] = useState(initialAddress?.locality || locationInfo?.city || '');
  const [region, setRegion] = useState(initialAddress?.region || locationInfo?.state || '');
  const [postalCode, setPostalCode] = useState(initialAddress?.postal_code || defaultPostalCode || '');
  const [postalCodeError, setPostalCodeError] = useState('');
  const [loggedAnnualIncomeVariant, setLoggedAnnualIncomeVariant] = useState(false);
  const { analytics } = useTracking(analyticsScreenName || ANALYTICS_SCREENS.PAYPLUS_ENROLL_SHIPPING);

  const fullAddress = useMemo(() => ({
    line_1: line1,
    line_2: line2,
    locality,
    region,
    postal_code: postalCode,
    country: 'US',
  }), [line1, line2, locality, region, postalCode]);

  const [suggestedAddress, setSuggestedAddress] = useState(result?.address);
  const [deliverability, setDeliverability] = useState(undefined);
  const [originalAddressDeliverabilityToken, setOriginalAddressDeliverabilityToken] = useState(undefined);

  const isDeliverableAddress = deliverability === 'deliverable';
  const addressIsPerfect = useMemo(
    () => isDeliverableAddress && addressesMatch(fullAddress, suggestedAddress),
    [fullAddress, isDeliverableAddress, suggestedAddress],
  );

  const handleCustomerDetailChange = React.useCallback((e) => {
    const { name, value } = e.target;
    setCustomerDetails({
      ...customerDetails,
      [name]: value,
    });
    setCustomerDetailErrors({
      ...customerDetailErrors,
      [name]: '',
    });
  }, [customerDetailErrors, customerDetails]);

  const handleBirthdateChange = React.useCallback(({ formattedValue }) => {
    if (INPUT_DATE_REGEX.test(formattedValue)) {
      // given date can be properly formatted
      const parsedDate = parse(formattedValue, 'MM/dd/yyyy', new Date());
      const formattedDate = format(parsedDate, 'yyyy-MM-dd');
      setCustomerDetails({
        ...customerDetails,
        birthdate: formattedDate,
      });
      setCustomerDetailErrors({
        ...customerDetailErrors,
        birthdate: '',
      });
    } else {
      // given date cannot be properly formatted. onBirthdateInputKeyDown will handle showing error
      setCustomerDetails({
        ...customerDetails,
        birthdate: formattedValue,
      });
    }
  }, [customerDetailErrors, customerDetails]);

  const handleAnnualIncomeChange = React.useCallback(({ formattedValue }) => {
    if (INPUT_INCOME_REGEX.test(formattedValue)) {
      setCustomerDetails({
        ...customerDetails,
        annualIncome: formattedValue,
      });
      setCustomerDetailErrors({
        ...customerDetailErrors,
        annualIncome: '',
      });
    } else {
      setCustomerDetails({
        ...customerDetails,
        annualIncome: formattedValue,
      });
    }
  }, [customerDetailErrors, customerDetails]);

  const onBirthdateInputBlur = () => {
    if (!checkDateValidity(customerDetails.birthdate)) {
      setCustomerDetailErrors({ ...customerDetailErrors, birthdate: BIRTHDATE_ERROR });
    } else if (!isAboveMinimumAge(customerDetails.birthdate)) {
      setCustomerDetailErrors({ ...customerDetailErrors, birthdate: 'You must be 18+' });
    } else {
      setCustomerDetailErrors({ ...customerDetailErrors, birthdate: '' });
    }
  };

  const onAnnualIncomeInputBlur = () => {
    if (customerDetails.annualIncome?.length) {
      const sanitizedAnnualIncome = customerDetails.annualIncome?.replace(/\$|,/g, '');
      if (sanitizedAnnualIncome <= 5) {
        setCustomerDetailErrors({ ...customerDetailErrors, annualIncome: ANNUAL_INCOME_ERROR });
      } else if (!/^\d+$/.test(sanitizedAnnualIncome)) {
        setCustomerDetailErrors({ annualIncome: 'Numbers only. Do not include symbols or decimals.' });
      } else {
        setCustomerDetailErrors({ annualIncome: '' });
      }
    }
  };

  const handleFirstNameChange = useCallback((e) => {
    setFirstName(e.target.value);
    setFirstNameError('');
  }, []);

  const handleLastNameChange = useCallback((e) => {
    setLastName(e.target.value);
    setLastNameError('');
  }, []);

  const handleLine1Change = useCallback((e) => {
    setLine1(e.target.value);
  }, []);

  const handleLine2Change = useCallback((e) => {
    setLine2(e.target.value);
  }, []);

  const handleLocalityChange = useCallback((e) => {
    setLocality(e.target.value);
  }, []);

  const handleRegionChange = useCallback((e) => {
    setRegion(e.target.value);
  }, []);

  const handlePostalCodeChange = useCallback((e) => {
    setPostalCode(e.target.value);
    setPostalCodeError('');
  }, []);

  const resetErrors = useCallback(() => {
    setPostalCodeError('');
    setFirstNameError('');
    setLastNameError('');
    setCustomerDetailErrors({
      number: '',
      birthdate: '',
      annualIncome: '',
      incomeConfirmed: '',
    });
  }, []);

  const handleEditAddress = useCallback(() => {
    setDeliverability(undefined);
    setSuggestedAddress(undefined);
  }, []);

  const handleSubmit = useCallback((e) => {
    let isValid = true;
    const newCustomerDetailErrors = {
      number: '',
      birthdate: '',
      annualIncome: '',
      incomeConfirmed: '',
    };

    if (firstName.trim() === '') {
      setFirstNameError('Please enter a valid first name');
      isValid = false;
    }

    if (lastName.trim() === '') {
      setLastNameError('Please enter a valid last name');
      isValid = false;
    }

    if (!postalCode || !isPostalCodeFromPayCountry(postalCode)) {
      setPostalCodeError('Please enter a valid postal code');
      isValid = false;
    }

    if (isEnrolling) {
      const { birthdate, number, annualIncome } = customerDetails;
      // Sanitize birthdate removing any characters other than digits and symbols / or -
      const sanitizedBirthdate = birthdate.replace(/(^(0-9|\/|-))/g, '');
      // Sanitize license number
      const sanitizedNumber = number.replace(/-/g, '');
      // Sanitize annual income
      const sanitizedAnnualIncome = annualIncome?.replace(/\$|,/g, '');
      const state = region.trim();

      // Validate customer details if given
      if (sanitizedNumber && state && !isValidLicense(state, sanitizedNumber)) {
        newCustomerDetailErrors.number = NUMBER_ERROR;
        isValid = false;
      }

      if (!sanitizedBirthdate || !isValidBirthdate(sanitizedBirthdate)) {
        newCustomerDetailErrors.birthdate = BIRTHDATE_ERROR;
        isValid = false;
      } else if (!isAboveMinimumAge(sanitizedBirthdate)) {
        newCustomerDetailErrors.birthdate = 'You must be 18+';
        isValid = false;
      }

      if (annualIncomeInputType === ANNUAL_INCOME_FEATURE_VARIANT.TEXTINPUT) {
        if (!sanitizedAnnualIncome || Number(sanitizedAnnualIncome) <= 5) {
          newCustomerDetailErrors.annualIncome = ANNUAL_INCOME_ERROR;
          isValid = false;
        } else if (!/^\d+$/.test(sanitizedAnnualIncome)) {
          newCustomerDetailErrors.annualIncome = 'Numbers only. Do not include symbols or decimals.';
          isValid = false;
        }
      }

      if (annualIncomeInputType === ANNUAL_INCOME_FEATURE_VARIANT.CHECKBOX && !incomeConfirmed) {
        isValid = false;
      }
    }

    e.preventDefault();

    if (isValid) {
      resetErrors();
      validateShippingAddress({
        address: fullAddress,
        program,
        token,
      }, analytics);
    } else if (isEnrolling && !isValid) {
      setCustomerDetailErrors(newCustomerDetailErrors);
    }
  }, [
    analytics,
    annualIncomeInputType,
    customerDetails,
    firstName,
    incomeConfirmed,
    isEnrolling,
    lastName,
    fullAddress,
    postalCode,
    program,
    region,
    resetErrors,
    token,
    validateShippingAddress,
  ]);

  const continueWithAddress = useCallback(async (address) => {
    if (triggerPrescreen && typeof triggerPrescreen === 'function') {
      // triggerPrescreen handles whether the user is eligible to get pre-screened
      await triggerPrescreen(address, firstName, lastName);
    }

    if (isEnrolling) {
      const { birthdate, number, annualIncome } = customerDetails;
      // Sanitize birthdate removing any characters other than digits and symbols / or -
      const sanitizedBirthdate = birthdate.replace(/(^(0-9|\/|-))/g, '');
      // Sanitize license number
      const sanitizedNumber = number.replace(/-/g, '');
      // Sanitize annual income
      const sanitizedAnnualIncome = annualIncome?.replace(/\$|,/g, '');

      updateCustomerProfile({
        birthdate: sanitizedBirthdate,
        number: sanitizedNumber,
        ...(annualIncomeInputType === ANNUAL_INCOME_FEATURE_VARIANT.CHECKBOX
          ? { incomeConfirmed }
          : { annualIncome: sanitizedAnnualIncome }
        ),
        address: {
          ...address,
          deliverability_token: originalAddressDeliverabilityToken,
        },
        'g-recaptcha-response': recaptchaResponse,
        firstName,
        lastName,
      });
      return;
    }

    saveShippingAddress({
      address: {
        ...address,
        deliverability_token: originalAddressDeliverabilityToken,
      },
      'g-recaptcha-response': recaptchaResponse,
      firstName,
      lastName,
    });
  }, [
    annualIncomeInputType,
    customerDetails,
    firstName,
    incomeConfirmed,
    isEnrolling,
    lastName,
    originalAddressDeliverabilityToken,
    recaptchaResponse,
    saveShippingAddress,
    triggerPrescreen,
    updateCustomerProfile,
  ]);

  const applySuggestedAddress = useCallback(() => {
    continueWithAddress(suggestedAddress);
  }, [continueWithAddress, suggestedAddress]);

  const applyProvidedAddress = useCallback(() => {
    continueWithAddress(fullAddress);
  }, [continueWithAddress, fullAddress]);

  const shouldUpdateLocationInfo = useMemo(
    () => !locality && !region && locationInfo?.city && locationInfo?.state,
    [locality, locationInfo, region],
  );

  const handleRecaptchaVerified = useCallback((response) => {
    setRecaptchaResponse(response);
  }, []);

  const handleIncomeConfirmation = React.useCallback(({ target }) => {
    setIncomeConfirmed(target.checked);
  }, []);

  // try to resolve wallet postal code to city/state
  useEffect(() => {
    if (defaultPostalCode && !isFetchingLocationInfo && !errorFetchingLocationInfo) {
      if (!locationInfo) {
        fetchLocationInfo(defaultPostalCode);
      } else if (shouldUpdateLocationInfo) {
        setLocality(locationInfo.city);
        setRegion(locationInfo.state);
      }
    }
  }, [
    locationInfo,
    isFetchingLocationInfo,
    errorFetchingLocationInfo,
    fetchLocationInfo,
    defaultPostalCode,
    shouldUpdateLocationInfo,
  ]);

  // Each time we get the result,
  // 1. preserve original deliverability token, so we can use it later if user wants to continue with provided address
  // 2. set deliverability so we can enforce appropriate address confirmation modal
  // 3. set suggested address provided in result, so that we can use it to confirm deliverable address
  useEffectWithDeepCompare(() => {
    if (result) {
      const { deliverability: addressDeliverability, address: addressSuggestion } = result;
      setOriginalAddressDeliverabilityToken(result.original_address_deliverability_token);
      setDeliverability(addressDeliverability);
      setSuggestedAddress(addressSuggestion);

      if (addressIsPerfect) {
        applySuggestedAddress();
      }
    }
  }, [addressIsPerfect, result]);

  const getDeliverabilityBasedAddressConfirmation = (closeModal) => {
    const handleContinue = () => {
      closeModal();
      applyProvidedAddress();
    };

    const handleAddressReconciliation = (e, shouldUseSuggestedAddress) => {
      closeModal();
      if (shouldUseSuggestedAddress) {
        applySuggestedAddress();
      } else {
        applyProvidedAddress();
      }
    };

    if (isDeliverableAddress) {
      return (
        <DeliverableAddressConfirmation
          providedAddress={fullAddress}
          suggestedAddress={suggestedAddress}
          onEditAddress={() => {
            closeModal();
            handleEditAddress();
          }}
          onContinue={handleAddressReconciliation}
        />
      );
    }

    return (
      <UndeliverableAddressConfirmation
        deliverability={deliverability}
        address={fullAddress}
        onEditAddress={handleEditAddress}
        onContinue={handleContinue}
      />
    );
  };

  useEffect(() => {
    if (!loggedAnnualIncomeVariant && annualIncomeInputType) {
      analytics.tagEvent({
        name: `${analyticsScreenName}_Rendered`,
        attributes: {
          enrollment_income_field_variant: annualIncomeInputType,
        },
      });
      setLoggedAnnualIncomeVariant(true);
    }
  }, [annualIncomeInputType, loggedAnnualIncomeVariant, analytics, analyticsScreenName]);

  if (error?.status === 409) {
    resetAddressError();

    return (
      <Redirect
        push
        to={`${program !== PayPrograms.Free ? `/${program.toLowerCase()}` : ''}/start`}
      />
    );
  }

  const showLoading = isValidating || isUpdating;
  const { isLoggedIn, goToStep, ...props } = rest;

  return (
    <Form
      aria-label="Shipping Address Form"
      action={isEnrolling ? '/customer-info' : '/shipping-address'}
      method="post"
      loading={showLoading}
      onSubmit={handleSubmit}
      {...props}
    >
      <Constraint className={cx('topContainer')} desktop={10}>
        {!!error && (
          <Modal
            size="sm"
            forceIsShowing={!!error}
            content={({ close }) => (
              <Fragment>
                <Header as="h3">Address Update Failed</Header>
                <Text as="p">
                  {error}
                </Text>
                <Button
                  secondary
                  onClick={(ev) => {
                    ev.preventDefault();
                    close();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  primary
                  as="a"
                  href="https://help.gasbuddy.com/hc/en-us/requests/new"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Contact Support
                </Button>
              </Fragment>
            )}
          />
        )}
        {!!deliverability && !addressIsPerfect && !error && (
          <Modal
            size="md"
            forceIsShowing={!showLoading}
            content={({ close }) => (
              <Fragment>
                {getDeliverabilityBasedAddressConfirmation(close)}
              </Fragment>
            )}
          />
        )}
        <Text as="p" className={cx('subtitle')}>
          {subheader}
        </Text>
      </Constraint>
      <FlexGrid className={cx('shippingAddressForm')} container>
        <FlexGrid.Column tablet={6} desktop={4} className={cx('customerDetailInputContainer')}>
          {!!firstNameError && (
            <Text color="orange">
              {firstNameError}
            </Text>
          )}
          <Input
            innerComponent={(
              <Tooltip
                content={(
                  <Text>
                    Please use your legal first name to avoid any verification issues.
                  </Text>
                )}
                icon={faInfo}
              />
            )}
            name="first_name"
            label="First Name"
            mask={/^[A-Za-z- ]+$/g}
            minLength={2}
            maxLength={15}
            onChange={handleFirstNameChange}
            autoComplete="given-name"
            value={firstName}
            required
          />
        </FlexGrid.Column>
        <FlexGrid.Column tablet={6} desktop={4} className={cx('customerDetailInputContainer')}>
          {!!lastNameError && (
            <Text color="orange">
              {lastNameError}
            </Text>
          )}
          <Input
            innerComponent={(
              <Tooltip
                icon={faInfo}
                content={(<Text>Please use your legal last name to avoid any verification issues.</Text>)}
              />
            )}
            name="last_name"
            label="Last Name"
            mask={/^[A-Za-z- ]+$/g}
            minLength={2}
            maxLength={20}
            onChange={handleLastNameChange}
            autoComplete="family-name"
            value={lastName}
            required
          />
        </FlexGrid.Column>
        <FlexGrid.Column tablet={12} desktop={8}>
          <Input
            name="line_1"
            label="Address"
            onChange={handleLine1Change}
            value={line1}
            mask={/^[A-Za-z0-9 .]+$/g}
            autoComplete="address-line1"
            required
            className={cx('formInput')}
          />
        </FlexGrid.Column>
        <FlexGrid.Column tablet={12} desktop={4}>
          <Input
            name="line_2"
            label="Apt/Suite"
            value={line2}
            mask={/^[A-Za-z0-9 .]+$/g}
            onChange={handleLine2Change}
            autoComplete="address-line2"
            className={cx('formInput')}
          />
        </FlexGrid.Column>
      </FlexGrid>
      <FlexGrid className={cx('shippingAddressForm')} container>
        <FlexGrid.Column tablet={8} desktop={5}>
          <Input
            name="locality"
            label="City"
            required
            mask={/^[A-Za-z0-9 .]+$/g}
            value={locality}
            onChange={handleLocalityChange}
            autoComplete="address-level2"
            className={cx('formInput')}
          />
        </FlexGrid.Column>
        <FlexGrid.Column tablet={4} desktop={3}>
          <StatesDropdown
            name="region"
            placeholder="State"
            required
            className={cx('statesDropdown', 'formInput', { filled: region })}
            value={region}
            onChange={handleRegionChange}
            autoComplete="address-level1"
          />
        </FlexGrid.Column>
        <FlexGrid.Column tablet={6} desktop={4}>
          <PostalCodeInput
            name="postal_code"
            label="Postal Code"
            required
            countries={PayCountries}
            value={postalCode}
            onChange={handlePostalCodeChange}
            error={postalCodeError}
            className={cx('formInput')}
          />
        </FlexGrid.Column>
      </FlexGrid>
      {!!(isEnrolling) && (
        <FlexGrid container>
          <FlexGrid.Column tablet={12} desktop={5}>
            <InputWithValidation
              name="number"
              label="Driver's License #"
              onChange={handleCustomerDetailChange}
              error={customerDetailErrors.number}
              value={customerDetails.number}
              isValid={customerDetails.number ? !customerDetailErrors.number?.length : undefined}
              required
            />
          </FlexGrid.Column>
          <FlexGrid.Column tablet={6} desktop={3}>
            <PatternFormat
              format="##/##/####"
              label="DOB"
              placeholder="mm/dd/yyyy"
              customInput={InputWithValidation}
              onValueChange={handleBirthdateChange}
              onBlur={onBirthdateInputBlur}
              mask={['m', 'm', 'd', 'd', 'y', 'y', 'y', 'y']}
              allowEmptyFormatting
              error={customerDetailErrors.birthdate}
              isValid={customerDetails.birthdate ? !customerDetailErrors.birthdate?.length : undefined}
            />
          </FlexGrid.Column>
          {(annualIncomeInputType === ANNUAL_INCOME_FEATURE_VARIANT.TEXTINPUT
          ) && (
            <FlexGrid.Column tablet={6} desktop={4}>
              <NumericFormat
                name="annualIncome"
                label="Annual Household Income"
                thousandSeparator=","
                prefix="$"
                onChange={handleCustomerDetailChange}
                customInput={InputWithValidation}
                error={customerDetailErrors.annualIncome}
                value={customerDetails.annualIncome}
                onBlur={onAnnualIncomeInputBlur}
                onValueChange={handleAnnualIncomeChange}
                isValid={customerDetails.annualIncome ? !customerDetailErrors.annualIncome?.length : undefined}
                maxLength={10}
                required
                allowNegative={false}
                innerComponent={(
                  <Tooltip
                    icon={faInfoCircle}
                    bordered={false}
                    content={(
                      <Text>
                        <strong>Why do we ask about income?</strong><br />
                        This helps us to ensure compliance with regulatory requirements and supports
                        the responsible use of our services.<br />
                        The information may also help us tailor features and protections to your needs.<br />
                        Have questions? <SupportLink>Contact support here</SupportLink>.
                      </Text>
                    )}
                  />
                )}
              />
              <Text as="small" className={cx('incomeExplanation')}>From salary, wages or investment income</Text>
            </FlexGrid.Column>
          )}
        </FlexGrid>
      )}
      {!!error && (
        <Text as="p" color="orange">
          {getErrorMessage(error, 'An error occurred while updating your shipping address')}
        </Text>
      )}
      {!!(isEnrolling
        && annualIncomeInputType === ANNUAL_INCOME_FEATURE_VARIANT.CHECKBOX
      ) && (
        <Constraint desktop={10}>
          <Checkbox
            name="incomeConfirmed"
            checked={incomeConfirmed}
            onChange={handleIncomeConfirmation}
            label={(
              <Text as="small">
                I confirm my income from salary, wages, or investment is equal to or more than $20,800 annually.&nbsp;
                <Tooltip
                  icon={faInfoCircle}
                  bordered={false}
                  content={(
                    <Text>
                      <strong>Why do we ask about income?</strong><br />
                      This helps us to ensure compliance with regulatory requirements and supports
                      the responsible use of our services.<br />
                      The information may also help us tailor features and protections to your needs.<br />
                      Have questions? <SupportLink>Contact support here</SupportLink>.
                    </Text>
                  )}
                />
              </Text>
            )}
          />
        </Constraint>
      )}
      <Constraint desktop={10}>
        <GoogleReCaptcha
          action="shipping"
          onVerify={handleRecaptchaVerified}
        />
        <FormActionButtons
          canSubmit={!showLoading
            && !!recaptchaResponse
            && (!isEnrolling || !!(customerDetails?.annualIncome || incomeConfirmed))
          }
          primaryButtonText={primaryButtonText}
        />
      </Constraint>
    </Form>
  );
}

ShippingAddressForm.propTypes = {
  address: AddressPropType,
  analyticsScreenName: PropTypes.string,
  annualIncomeInputType: PropTypes.string,
  basePath: PropTypes.string,
  defaultPostalCode: PropTypes.string,
  email: PropTypes.string,
  error: PropTypes.shape({
    code: PropTypes.string,
    domain: PropTypes.string,
    message: PropTypes.string,
    status: PropTypes.number,
  }),
  errorFetchingLocationInfo: PropTypes.string,
  fetchLocationInfo: PropTypes.func,
  firstName: PropTypes.string,
  isFetchingLocationInfo: PropTypes.bool,
  isEnrolling: PropTypes.bool,
  isUpdating: PropTypes.bool,
  isValidating: PropTypes.bool,
  lastName: PropTypes.string,
  locationInfo: PropTypes.shape({
    city: PropTypes.string,
    state: PropTypes.string,
  }),
  primaryButtonText: PropTypes.string,
  program: ProgramPropType,
  subheader: PropTypes.string,
  resetAddressError: PropTypes.func,
  result: ShippingAddressPropType,
  saveShippingAddress: PropTypes.func,
  token: PropTypes.string,
  triggerPrescreen: PropTypes.func,
  updateCustomerProfile: PropTypes.func,
  validateShippingAddress: PropTypes.func,
};

ShippingAddressForm.defaultProps = {
  address: undefined,
  analyticsScreenName: undefined,
  annualIncomeInputType: ANNUAL_INCOME_FEATURE_VARIANT.CHECKBOX,
  basePath: undefined,
  defaultPostalCode: undefined,
  email: '',
  error: undefined,
  errorFetchingLocationInfo: undefined,
  fetchLocationInfo: () => { },
  firstName: undefined,
  isEnrolling: false,
  isFetchingLocationInfo: false,
  isUpdating: false,
  isValidating: false,
  lastName: undefined,
  locationInfo: undefined,
  primaryButtonText: undefined,
  program: undefined,
  resetAddressError: () => { },
  result: undefined,
  saveShippingAddress: () => { },
  subheader: 'We need your legal name and shipping address so we know where to send your new Pay with GasBuddy+™ card.',
  token: undefined,
  triggerPrescreen: undefined,
  updateCustomerProfile: () => {},
  validateShippingAddress: () => { },
};
